import Cookies from 'js-cookie';
import { debounce } from 'lodash';
import { useEffect, useRef } from 'react';
import PurchaseViewWrapper from './PurchaseViewWrapper';
import { DEBOUNCE_DELAY } from '../../../constants';
import { type BeneficiaryType } from '../../../dto/beneficiary';
import { useBackgroundInfoGuard } from '../../../hooks/backgroundInfoGuard';
import CoverageSection from '../../components/CoverageSection';
import Notifications from '../../components/Notifications';
import ScreenReaderCartUpdates from '../../components/ShoppingCart/ScreenReaderCartUpdates';
import { type PurchasePathState } from '../../types/initialState';
import AuthenticatedPurchaseView from '../AuthenticatedPurchaseView';
import InitialPurchaseView from '../InitialPurchaseView';
import RatingLoading from '../RatingLoading';
import {
  AnalyticsPhaseCategories,
  AnalyticsPhases,
  type AnalyticsProductProps,
  trackPhase,
} from 'api/analytics-api';
import { ProfilingResult } from 'dto/ProfilingResult';
import { useSelectedPremiums } from 'state/selectors/coverageSelectors';
import { getTestGroup } from 'state/state';
import { useAppStore } from 'state/store';
import { type TestGroup } from 'types';
import { getInitialState, isOperationalDevelopmentCookieAccepted } from 'utils';

const PurchaseView = () => {
  const {
    authenticatedCustomer,
    marketableProduct,
    profilingResult,
    ageInvalid,
    conductABTesting,
  } = getInitialState() as PurchasePathState;

  const selectedBeneficiary = useAppStore.use.selectedBeneficiary();
  const setTestGroup = useAppStore.use.setTestGroup();
  const setUserHasSeenCoverages = useAppStore.use.setUserHasSeenCoverages();
  const backgroundInfoForm = useAppStore.use.backgroundInfoForm();
  const ratingLoading = useAppStore.use.ratingLoading();
  const selectedPremiums = useSelectedPremiums();
  const coveragePremiums = useAppStore.use.coveragePremiums();
  const selectedCoverages = useAppStore.use.selectedCoverages();

  const isBackgroundInfoValid = useBackgroundInfoGuard(
    backgroundInfoForm,
    authenticatedCustomer,
    marketableProduct
  );

  useEffect(() => {
    if (abTestingAllowed()) {
      setTestGroup(
        (Cookies.get('ab-test-group') as TestGroup) ??
          getTestGroup(backgroundInfoForm.dateOfBirth)
      );
    }
  }, []);

  // Set userHasSeenCoverages when user first views the PurchaseView with valid background info
  useEffect(() => {
    if (isBackgroundInfoValid) {
      setUserHasSeenCoverages(true);
    }
  }, [isBackgroundInfoValid, setUserHasSeenCoverages]);

  const abTestingAllowed = () => {
    return conductABTesting && isOperationalDevelopmentCookieAccepted();
  };

  const isTrackingPurchasePageAnalytics = () => {
    return (
      Object.keys(selectedPremiums).length > 0 &&
      !ageInvalid &&
      profilingResult !== ProfilingResult.BLOCK
    );
  };

  const debouncedTrackPhase = useRef(
    debounce((phase: AnalyticsPhases, params: AnalyticsProductProps) => {
      trackPhase(AnalyticsPhaseCategories.STEP, phase, params);
    }, DEBOUNCE_DELAY)
  );

  // effect hook to track analytics
  useEffect(() => {
    if (isTrackingPurchasePageAnalytics()) {
      if (!authenticatedCustomer) {
        debouncedTrackPhase.current(AnalyticsPhases.PRICE, {
          coveragePremiums,
          selectedSumInsuredPremiums: selectedPremiums,
          selectedCoverages,
        });
      } else {
        debouncedTrackPhase.current(
          AnalyticsPhases.BENEFICIARIES_AND_DOCUMENTS,
          {
            coveragePremiums,
            selectedSumInsuredPremiums: selectedPremiums,
            beneficiaryInfo: selectedBeneficiary as BeneficiaryType,
            selectedCoverages,
          }
        );
      }
    }
  }, [
    Object.values(selectedPremiums)
      .map(
        (premium) => `${premium?.sumInsured}${premium?.deductibleDays ?? ''}`
      )
      .join(','),
  ]);

  if (ratingLoading) {
    return <RatingLoading />;
  }

  const commonContent = (
    <>
      <Notifications />
      <ScreenReaderCartUpdates />
      <CoverageSection />
    </>
  );

  if (!isBackgroundInfoValid) {
    return null;
  }

  return (
    <PurchaseViewWrapper>
      {authenticatedCustomer ? (
        <AuthenticatedPurchaseView>{commonContent}</AuthenticatedPurchaseView>
      ) : (
        <InitialPurchaseView>{commonContent}</InitialPurchaseView>
      )}
    </PurchaseViewWrapper>
  );
};

export default PurchaseView;
